import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useColorModeValue,
  Heading,
  Card,
  CardBody,
  Select,
  useToast,
  Tag,
  TagLabel,
  TagCloseButton,
  InputGroup,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { ArrowBackIcon, CheckIcon } from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { associateAPI } from '../services/api';

const AddAssociate: React.FC = () => {
  // For skill suggestion hover color
  const listItemHoverBg = useColorModeValue('gray.100', 'gray.700');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const emergencyPhoneInputRef = useRef<HTMLInputElement>(null);

  const isEditMode = Boolean(id);

  // State for country codes
  const [selectedCountryCode, setSelectedCountryCode] = useState('+91');
  const [selectedEmergencyCountryCode, setSelectedEmergencyCountryCode] = useState('+91');
  const [menuWidth, setMenuWidth] = useState<number>(200);
  const [emergencyMenuWidth, setEmergencyMenuWidth] = useState<number>(200);
  const countryCodes = ['+1', '+44', '+91', '+61', '+49', '+33', '+81'];

  type AssociateFormData = {
    empid: string; // Employee ID entered by user
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    designation: string;
    department: string;
    joinDate: string;
    salary: string;
    // status removed from form
    skills: string;
    experience: string;
    education: string;
    emergencyContact: {
      name: string;
      contactNumber: string;
    };
    deleted: boolean;
    userId: string;
  };

  const initialData: AssociateFormData = {
    empid: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  designation: '',
    department: '',
    joinDate: '',
    salary: '',
    skills: '',
    experience: '',
    education: '',
    emergencyContact: {
      name: '',
      contactNumber: '',
    },
    deleted: false,
    userId: user?._id || 'demo-user',
  };

  const [formData, setFormData] = useState<AssociateFormData>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [designations, setDesignations] = useState<any[]>([]);
  const [currentSkill, setCurrentSkill] = useState('');
  const [skillSuggestions, setSkillSuggestions] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);

  const suggestedSkills = [
    'JavaScript', 'Python', 'Java', 'React', 'Node.js', 'TypeScript', 'SQL',
    'HTML', 'CSS', 'Angular', 'Vue.js', 'AWS', 'Docker', 'Kubernetes',
    'MongoDB', 'PostgreSQL', 'GraphQL', 'Git', 'Agile', 'Scrum'
  ];

  useEffect(() => {
    if (isEditMode && id) {
      fetchAssociate(id);
    }
    // fetch designations for the designation select
   const fetchDesignations = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch((process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + '/designations', {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        const data = await res.json();
        setDesignations(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch designations', err);
      }
    };
    fetchDesignations();
  }, [id]);

  // When designations load and we have a designation value from the backend,
  // map it to the designation _id and department so the select shows correct value.
  useEffect(() => {
    if (!designations || designations.length === 0) return;
    if (!formData || !formData.designation) return;
    try {
      const current = formData.designation;
      const matched = designations.find(d => String(d._id) === String(current) || String(d.title).toLowerCase() === String(current).toLowerCase());
      if (matched) {
        setFormData(prev => ({ ...prev, designation: String(matched._id), department: matched.department || prev.department }));
      }
    } catch (e: unknown) {
      // ignore mapping errors - safely extract message
      const msg = e instanceof Error ? e.message : (typeof e === 'object' && e && 'message' in e) ? (e as any).message : String(e);
      console.warn('Designation mapping (frontend) failed', msg);
    }
  }, [designations, formData.designation]);

  useEffect(() => {
    // Convert skills string to array when editing
    if (formData.skills) {
      setSelectedSkills(formData.skills.split(',').map(skill => skill.trim()).filter(skill => skill));
    }
  }, [formData.skills]);

  useEffect(() => {
    // Update suggestions based on current input
    if (currentSkill) {
      const filtered = suggestedSkills.filter(skill =>
        skill.toLowerCase().startsWith(currentSkill.toLowerCase())
      );
      setSkillSuggestions(filtered);
    } else {
      setSkillSuggestions([]);
    }
  }, [currentSkill]);

  // Set menu width to match phone input
  useEffect(() => {
    if (phoneInputRef.current) {
      setMenuWidth(phoneInputRef.current.offsetWidth);
    }
  }, [phoneInputRef.current]);

  // Set menu width to match emergency phone input
  useEffect(() => {
    if (emergencyPhoneInputRef.current) {
      setEmergencyMenuWidth(emergencyPhoneInputRef.current.offsetWidth);
    }
  }, [emergencyPhoneInputRef.current]);

  // Update menu widths on window resize
  useEffect(() => {
    const onResize = () => {
      if (phoneInputRef.current) setMenuWidth(phoneInputRef.current.offsetWidth);
      if (emergencyPhoneInputRef.current) setEmergencyMenuWidth(emergencyPhoneInputRef.current.offsetWidth);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchAssociate = async (associateId: string) => {
    try {
      const res = await associateAPI.getAssociateById(associateId);
      let emergencyContactObj = { name: '', contactNumber: '' };
      if (typeof res.data.emergencyContact === 'string') {
        emergencyContactObj = { name: '', contactNumber: res.data.emergencyContact };
      } else if (res.data.emergencyContact && typeof res.data.emergencyContact === 'object') {
        emergencyContactObj = {
          name: res.data.emergencyContact.name || '',
          contactNumber: res.data.emergencyContact.contactNumber || '',
        };
      }
      // Parse phone number into country code and local number
      let phoneVal = res.data.phone || '';
      let parsedNumber = '';
      let parsedCode = selectedCountryCode;
      if (phoneVal && phoneVal.startsWith('+')) {
        const sortedCodes = countryCodes.slice().sort((a, b) => b.length - a.length);
        const match = sortedCodes.find(code => phoneVal.startsWith(code));
        if (match) {
          parsedCode = match;
          parsedNumber = phoneVal.slice(match.length);
        } else {
          parsedCode = selectedCountryCode;
          parsedNumber = phoneVal.replace(/^\+/, '');
        }
      } else {
        parsedNumber = phoneVal;
      }
      // Parse emergency contact number into country code and local number
      let emergencyPhoneVal = emergencyContactObj.contactNumber || '';
      let parsedEmergencyNumber = '';
      let parsedEmergencyCode = selectedEmergencyCountryCode;
      if (emergencyPhoneVal && emergencyPhoneVal.startsWith('+')) {
        const sortedCodes = countryCodes.slice().sort((a, b) => b.length - a.length);
        const match = sortedCodes.find(code => emergencyPhoneVal.startsWith(code));
        if (match) {
          parsedEmergencyCode = match;
          parsedEmergencyNumber = emergencyPhoneVal.slice(match.length);
        } else {
          parsedEmergencyCode = selectedEmergencyCountryCode;
          parsedEmergencyNumber = emergencyPhoneVal.replace(/^\+/, '');
        }
      } else {
        parsedEmergencyNumber = emergencyPhoneVal;
      }
      // normalize joinDate for HTML date input (backend stores DD-MM-YYYY)
      const rawJoin = res.data.joinDate || '';
      let joinForInput = '';
      const ddmmyyyy = rawJoin.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      const yyyymmdd = rawJoin.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (ddmmyyyy) {
        joinForInput = `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`;
      } else if (yyyymmdd) {
        joinForInput = rawJoin;
      } else if (rawJoin) {
        const parsed = Date.parse(rawJoin);
        if (!isNaN(parsed)) {
          const dt = new Date(parsed);
          const mm = String(dt.getMonth() + 1).padStart(2, '0');
          const dd = String(dt.getDate()).padStart(2, '0');
          const yyyy = dt.getFullYear();
          joinForInput = `${yyyy}-${mm}-${dd}`;
        }
      }
      // Split name into firstName and lastName
      const [firstName = '', lastName = ''] = res.data.name ? res.data.name.split(' ') : ['', ''];
      setSelectedCountryCode(parsedCode);
      setSelectedEmergencyCountryCode(parsedEmergencyCode);
      setFormData({
        empid: res.data._id || '',
        firstName,
        lastName,
        email: res.data.email,
        phone: parsedNumber,
  designation: res.data.designation,
        department: res.data.department,
        joinDate: joinForInput,
        salary: res.data.salary || '',
        skills: res.data.skills || '',
        experience: res.data.experience || '',
        education: res.data.education || '',
        emergencyContact: {
          name: emergencyContactObj.name,
          contactNumber: parsedEmergencyNumber,
        },
        deleted: res.data.deleted || false,
        userId: res.data.userId || user?._id || 'demo-user',
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : (typeof err === 'object' && err && 'message' in err) ? (err as any).message : String(err);
      console.error('Fetch Associate Error:', msg);
      toast({
        title: 'Error',
        description: 'Failed to fetch associate details. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleAddSkill = () => {
    if (currentSkill && !selectedSkills.includes(currentSkill)) {
      const newSkills = [...selectedSkills, currentSkill];
      setSelectedSkills(newSkills);
      setFormData({ ...formData, skills: newSkills.join(', ') });
      setCurrentSkill('');
      setSkillSuggestions([]);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleRemoveSkill = (skill: string) => {
    const newSkills = selectedSkills.filter(s => s !== skill);
    setSelectedSkills(newSkills);
    setFormData({ ...formData, skills: newSkills.join(', ') });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate phone number length (max 10 digits)
      if (formData.phone && formData.phone.length > 10) {
        setIsSubmitting(false);
        toast({
          title: 'Invalid phone number',
          description: 'Phone number should not be more than 10 digits.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      // Validate emergency contact number length (max 10 digits)
      if (formData.emergencyContact.contactNumber && formData.emergencyContact.contactNumber.length > 10) {
        setIsSubmitting(false);
        toast({
          title: 'Invalid emergency contact number',
          description: 'Emergency contact number should not be more than 10 digits.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Concatenate firstName and lastName for backend
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      // Compose full phone number with country code
      const composedPhone = `${selectedCountryCode}${formData.phone}`;
      // Compose full emergency contact number with country code
      const composedEmergencyPhone = `${selectedEmergencyCountryCode}${formData.emergencyContact.contactNumber}`;
      // Convert joinDate from HTML input (YYYY-MM-DD) to backend (DD-MM-YYYY)
      let joinForBackend = '';
      if (formData.joinDate) {
        const m = String(formData.joinDate).match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (m) {
          joinForBackend = `${m[3]}-${m[2]}-${m[1]}`; // dd-mm-yyyy
        } else {
          // fallback: try parsing
          const parsed = Date.parse(formData.joinDate);
          if (!isNaN(parsed)) {
            const dt = new Date(parsed);
            const mm = String(dt.getMonth() + 1).padStart(2, '0');
            const dd = String(dt.getDate()).padStart(2, '0');
            const yyyy = dt.getFullYear();
            joinForBackend = `${dd}-${mm}-${yyyy}`;
          }
        }
      }

      // Prepare payload
      const payload = {
        ...formData,
        _id: formData.empid, // send empid as _id
        name: fullName,
        phone: composedPhone,
        status: 'active' as 'active', // always set status to active with correct type
        joinDate: joinForBackend,
        emergencyContact: {
          name: formData.emergencyContact.name,
          contactNumber: composedEmergencyPhone,
        },
      };

      if (isEditMode) {
        if (!id) {
          throw new Error('Associate ID is missing');
        }
        await associateAPI.updateAssociate(id, payload);
        toast({
          title: 'Associate Updated Successfully',
          description: `${fullName}'s details have been updated.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        await associateAPI.createAssociate(payload);
        toast({
          title: 'Associate Added Successfully',
          description: `${fullName} has been added to your team.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
      navigate('/associates');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : (typeof err === 'object' && err && 'message' in err) ? (err as any).message : String(err);
      console.error('Save Associate Error:', msg);
      const anyErr = err as any;
      const errorMessage = anyErr?.response?.status === 404
        ? 'Backend server not found. Please ensure the server is running.'
        : anyErr?.response?.data?.message || 'Failed to save associate. Please try again.';
      toast({
        title: isEditMode ? 'Error Updating Associate' : 'Error Adding Associate',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/associates');
  };

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box>
      <Box mb={8}>
        <HStack spacing={4} mb={4}>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="ghost"
            onClick={handleCancel}
            color="gray.600"
          >
            Back to Associates
          </Button>
        </HStack>
        <Heading size="lg" color="gray.800" mb={2}>
          {isEditMode ? 'Edit Associate' : 'Onboard New Associate'}
        </Heading>
        <Text color="gray.600">
          {isEditMode ? 'Update associate details and save changes.' : 'Add a new team member to your organization'}
        </Text>
      </Box>

      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody p={8}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={6} align="stretch">
              <Box>
                <VStack spacing={4} align="stretch">
                  <Heading size="md" color="gray.700" mb={2}>
                    Basic Information
                  </Heading>
                  <HStack spacing={4} w="full">
                    <FormControl isRequired>
                      <FormLabel>Employee ID</FormLabel>
                      <Input
                        value={formData.empid}
                        onChange={(e) => setFormData({ ...formData, empid: e.target.value })}
                        placeholder="Enter employee ID (empid)"
                        className="form-input"
                        maxWidth="200px"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>First Name</FormLabel>
                      <Input
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        placeholder="Enter first name"
                        className="form-input"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Last Name</FormLabel>
                      <Input
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        placeholder="Enter last name"
                        className="form-input"
                      />
                    </FormControl>
                  </HStack>

                  <HStack spacing={4} w="full">
                    <FormControl isRequired>
                      <FormLabel>Designation</FormLabel>
                      <Select
                        value={formData.designation}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          // Find designation by _id
                          const sel = designations.find(d => String(d._id) === String(selectedId));
                          setFormData({
                            ...formData,
                            designation: selectedId,
                            department: sel?.department || '',
                          });
                        }}
                        placeholder="Select designation"
                        className="form-input"
                        maxWidth="300px"
                      >
                        {designations.map(d => (
                          <option key={d._id} value={String(d._id)}>{d.title}</option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Email Address</FormLabel>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="Enter email address"
                        className="form-input"
                        maxWidth="300px"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Phone Number</FormLabel>
                      <HStack>
                        <Menu>
                          <MenuButton as={Button} size="sm" variant="outline" minW="60px">
                            {selectedCountryCode}
                          </MenuButton>
                          <MenuList minW={`${menuWidth}px`}>
                            <MenuItem onClick={() => setSelectedCountryCode('+1')}>+1 (USA)</MenuItem>
                            <MenuItem onClick={() => setSelectedCountryCode('+44')}>+44 (UK)</MenuItem>
                            <MenuItem onClick={() => setSelectedCountryCode('+91')}>+91 (India)</MenuItem>
                            <MenuItem onClick={() => setSelectedCountryCode('+61')}>+61 (Australia)</MenuItem>
                            <MenuItem onClick={() => setSelectedCountryCode('+49')}>+49 (Germany)</MenuItem>
                            <MenuItem onClick={() => setSelectedCountryCode('+33')}>+33 (France)</MenuItem>
                            <MenuItem onClick={() => setSelectedCountryCode('+81')}>+81 (Japan)</MenuItem>
                          </MenuList>
                        </Menu>
                        <Input
                          ref={phoneInputRef}
                          value={formData.phone}
                          onChange={(e) => {
                            const digitsOnly = e.target.value.replace(/\D/g, '');
                            const limited = digitsOnly.slice(0, 10);
                            setFormData({ ...formData, phone: limited });
                          }}
                          placeholder="Enter phone number"
                          className="form-input"
                          maxWidth="300px"
                        />
                      </HStack>
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              <Box>
                <Heading size="md" color="gray.700" mb={4}>
                  Employment Details
                </Heading>
                <VStack spacing={4}>
                  <HStack spacing={4} w="full">
                    <FormControl isRequired>
                      <FormLabel>Department</FormLabel>
                      <Input
                        value={formData.department}
                        readOnly
                        placeholder="Department will populate based on selected designation"
                        className="form-input"
                        maxWidth="300px"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Join Date</FormLabel>
                      <Input
                        type="date"
                        value={formData.joinDate}
                        onChange={(e) => setFormData({ ...formData, joinDate: e.target.value })}
                        className="form-input"
                      />
                    </FormControl>
                  </HStack>

                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>Salary</FormLabel>
                      <Input
                        type="number"
                        value={formData.salary}
                        onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                        placeholder="Enter salary amount"
                        className="form-input"
                      />
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              <Box>
                <Heading size="md" color="gray.700" mb={4}>
                  Skills & Experience
                </Heading>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Skill set</FormLabel>
                    <HStack spacing={2}>
                      <Input
                        ref={inputRef}
                        value={currentSkill}
                        onChange={(e) => setCurrentSkill(e.target.value)}
                        placeholder="Enter key skills"
                        size="md"
                        className="form-input"
                        maxWidth="300px"
                      />
                      <Button
                        size="sm"
                        onClick={handleAddSkill}
                        isDisabled={!currentSkill || selectedSkills.includes(currentSkill)}
                      >
                        Add
                      </Button>
                    </HStack>
                    {skillSuggestions.length > 0 && (
                      <Box
                        position="absolute"
                        bg={cardBg}
                        border="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                        mt={10}
                        zIndex={10}
                        width="300px"
                        maxHeight="200px"
                        overflowY="auto"
                      >
                        <List>
                          {skillSuggestions.map((skill) => (
                            <ListItem
                              key={skill}
                              p={2}
                              _hover={{ bg: listItemHoverBg }}
                              cursor="pointer"
                              onClick={() => {
                                setCurrentSkill(skill);
                                setSkillSuggestions([]);
                                if (inputRef.current) {
                                  inputRef.current.focus();
                                }
                              }}
                            >
                              {skill}
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                    <HStack mt={2} flexWrap="wrap" spacing={2}>
                      {selectedSkills.map((skill) => (
                        <Tag
                          key={skill}
                          size="md"
                          borderRadius="full"
                          variant="solid"
                          colorScheme="gray"
                          opacity={0.7}
                        >
                          <TagLabel>{skill}</TagLabel>
                          <TagCloseButton onClick={() => handleRemoveSkill(skill)} />
                        </Tag>
                      ))}
                    </HStack>
                  </FormControl>
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>Years of Experience</FormLabel>
                      <Input
                        value={formData.experience}
                        onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                        placeholder="e.g., 5 years"
                        className="form-input"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Education</FormLabel>
                      <Input
                        value={formData.education}
                        onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                        placeholder="e.g., Bachelor's in Computer Science"
                        className="form-input"
                      />
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              <Box>
                <Heading size="md" color="gray.700" mb={4}>
                  Additional Information
                </Heading>
                <VStack spacing={4} align="stretch">
                  <HStack spacing={4} w="full">
                    <FormControl isRequired>
                      <FormLabel>Emergency Contact Name</FormLabel>
                      <Input
                        value={formData.emergencyContact.name}
                        onChange={(e) => setFormData({ ...formData, emergencyContact: { ...formData.emergencyContact, name: e.target.value } })}
                        placeholder="Enter emergency contact name"
                        className="form-input"
                        maxWidth="300px"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Emergency Contact Number</FormLabel>
                      <HStack>
                        <Menu>
                          <MenuButton as={Button} size="sm" variant="outline" minW="60px">
                            {selectedEmergencyCountryCode}
                          </MenuButton>
                          <MenuList minW={`${emergencyMenuWidth}px`}>
                            <MenuItem onClick={() => setSelectedEmergencyCountryCode('+1')}>+1 (USA)</MenuItem>
                            <MenuItem onClick={() => setSelectedEmergencyCountryCode('+44')}>+44 (UK)</MenuItem>
                            <MenuItem onClick={() => setSelectedEmergencyCountryCode('+91')}>+91 (India)</MenuItem>
                            <MenuItem onClick={() => setSelectedEmergencyCountryCode('+61')}>+61 (Australia)</MenuItem>
                            <MenuItem onClick={() => setSelectedEmergencyCountryCode('+49')}>+49 (Germany)</MenuItem>
                            <MenuItem onClick={() => setSelectedEmergencyCountryCode('+33')}>+33 (France)</MenuItem>
                            <MenuItem onClick={() => setSelectedEmergencyCountryCode('+81')}>+81 (Japan)</MenuItem>
                          </MenuList>
                        </Menu>
                        <Input
                          ref={emergencyPhoneInputRef}
                          value={formData.emergencyContact.contactNumber}
                          onChange={(e) => {
                            const digitsOnly = e.target.value.replace(/\D/g, '');
                            const limited = digitsOnly.slice(0, 10);
                            setFormData({ ...formData, emergencyContact: { ...formData.emergencyContact, contactNumber: limited } });
                          }}
                          placeholder="Enter emergency contact number"
                          className="form-input"
                          maxWidth="300px"
                        />
                      </HStack>
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              <HStack spacing={4} justify="flex-end" pt={4}>
                <Button
                  variant="ghost"
                  onClick={handleCancel}
                  size="lg"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="primary"
                  leftIcon={<CheckIcon />}
                  isLoading={isSubmitting}
                  loadingText={isEditMode ? 'Updating Associate...' : 'Adding Associate...'}
                  size="lg"
                >
                  {isEditMode ? 'Save Changes' : 'Onboard Associate'}
                </Button>
              </HStack>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
};

export default AddAssociate;